import { displayText } from '../../../../../../terminology/store';
import { useTerminology } from '../../../../../../terminology/TerminologyContext';
import React, { useState, useEffect, useContext } from 'react';
import { Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { getCases, getCaseFolders, moveCaseFolder, moveDocument } from '../../../../../services/api';
import ThemeContext from '../../../../../ThemeContext';

const MoveModal = ({ show, onHide, items, itemType, currentCaseId, onSuccess }) => {
  useTerminology();
  const { theme } = useContext(ThemeContext);
  const [cases, setCases] = useState([]);
  const [folders, setFolders] = useState([]);
  const [selectedCase, setSelectedCase] = useState('');
  const [selectedFolder, setSelectedFolder] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingFolders, setIsFetchingFolders] = useState(false);

  useEffect(() => {
    if (show) {
      fetchCases();
    }
  }, [show]);

  useEffect(() => {
    if (selectedCase) {
      fetchFolders(selectedCase);
    } else {
      setFolders([]);
      setSelectedFolder('');
    }
  }, [selectedCase]);

  const fetchCases = async () => {
    try {
      const response = await getCases();
      setCases(response.data.data || response.data);
    } catch (error) {
      toast.error(`${displayText("Failed to fetch cases: ")}${error.response?.data?.message || error.message}${displayText("")}`, {
        style: {
          backgroundColor: theme.colors.bgPrimary,
          border: `1px solid ${theme.colors.borderColor}`,
          color: theme.colors.danger,
        },
      });
    }
  };

  const fetchFolders = async (caseId) => {
    try {
      setIsFetchingFolders(true);
      const response = await getCaseFolders(caseId);
      setFolders(response.data);
    } catch (error) {
      toast.error(`Failed to fetch folders: ${error.response?.data?.message || error.message}`, {
        style: {
          backgroundColor: theme.colors.bgPrimary,
          border: `1px solid ${theme.colors.borderColor}`,
          color: theme.colors.danger,
        },
      });
    } finally {
      setIsFetchingFolders(false);
    }
  };

  const handleMove = async () => {
    if (itemType === 'folder' && !selectedCase) {
      toast.warning(displayText('Please select a target case'), {
        style: {
          backgroundColor: theme.colors.bgPrimary,
          border: `1px solid ${theme.colors.borderColor}`,
          color: theme.colors.warning,
        },
      });
      return;
    }

    if (itemType === 'document' && !selectedFolder) {
      toast.warning('Please select a target folder', {
        style: {
          backgroundColor: theme.colors.bgPrimary,
          border: `1px solid ${theme.colors.borderColor}`,
          color: theme.colors.warning,
        },
      });
      return;
    }

    try {
      setIsLoading(true);
      
      // Debug log
      console.log('🔍 Moving:', {
        itemType,
        items,
        selectedCase,
        selectedFolder,
        dataToSend: itemType === 'folder' 
          ? { target_case_id: parseInt(selectedCase) }
          : { target_folder_id: parseInt(selectedFolder) }
      });
      
      if (itemType === 'folder') {
        for (const folderId of items) {
          await moveCaseFolder(folderId, { target_case_id: parseInt(selectedCase) });
        }
        toast.success('Folders moved successfully', {
          style: {
            backgroundColor: theme.colors.bgPrimary,
            border: `1px solid ${theme.colors.borderColor}`,
            color: theme.colors.success,
          },
        });
      } else if (itemType === 'document') {
        for (const documentId of items) {
          await moveDocument(documentId, { target_folder_id: parseInt(selectedFolder) });
        }
        toast.success('Documents moved successfully', {
          style: {
            backgroundColor: theme.colors.bgPrimary,
            border: `1px solid ${theme.colors.borderColor}`,
            color: theme.colors.success,
          },
        });
      }

      onSuccess();
      handleClose();
    } catch (error) {
      console.error('Move error:', error.response?.data);
      toast.error(`Failed to move ${itemType}s: ${error.response?.data?.message || error.message}`, {
        style: {
          backgroundColor: theme.colors.bgPrimary,
          border: `1px solid ${theme.colors.borderColor}`,
          color: theme.colors.danger,
        },
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedCase('');
    setSelectedFolder('');
    setFolders([]);
    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header
        style={{
          backgroundColor: theme.colors.primary,
          color: theme.colors.textWhite,
          borderBottom: 'none',
        }}
      >
        <Modal.Title style={{ fontSize: 'clamp(12px, 2.5vw, 14px)' }}>
          Move {itemType === 'folder' ? 'Folders' : 'Documents'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body
        style={{
          backgroundColor: theme.colors.bgPrimary,
          color: theme.colors.textPrimary,
        }}
      >
        <p style={{ fontSize: 'clamp(9px, 2vw, 10px)', marginBottom: '1rem' }}>
          Moving {items.length} {itemType}(s)
        </p>

        {itemType === 'folder' ? (
          <div style={{ marginBottom: '1rem' }}>
            <label
              htmlFor="targetCase"
              style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontSize: 'clamp(9px, 2vw, 10px)',
                fontWeight: 'bold',
              }}
            >{displayText(" Select Target Case ")}</label>
            <select
              id="targetCase"
              value={selectedCase}
              onChange={(e) => setSelectedCase(e.target.value)}
              style={{
                width: '100%',
                padding: 'clamp(6px, 2vw, 8px)',
                fontSize: 'clamp(9px, 2vw, 10px)',
                border: `1px solid ${theme.colors.borderColor}`,
                borderRadius: '5px',
                backgroundColor: theme.colors.bgSecondary,
                color: theme.colors.textPrimary,
              }}
              disabled={isLoading}
            >
              <option value="">{displayText("-- Select a case --")}</option>
              {cases.map((caseItem) => (
                <option key={caseItem.id} value={caseItem.id}>
                  {caseItem.case_name}
                </option>
              ))}
            </select>
          </div>
        ) : (
          <>
            <div style={{ marginBottom: '1rem' }}>
              <label
                htmlFor="targetCase"
                style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  fontSize: 'clamp(9px, 2vw, 10px)',
                  fontWeight: 'bold',
                }}
              >{displayText(" Select Target Case ")}</label>
              <select
                id="targetCase"
                value={selectedCase}
                onChange={(e) => setSelectedCase(e.target.value)}
                style={{
                  width: '100%',
                  padding: 'clamp(6px, 2vw, 8px)',
                  fontSize: 'clamp(9px, 2vw, 10px)',
                  border: `1px solid ${theme.colors.borderColor}`,
                  borderRadius: '5px',
                  backgroundColor: theme.colors.bgSecondary,
                  color: theme.colors.textPrimary,
                }}
                disabled={isLoading}
              >
                <option value="">{displayText("-- Select a case --")}</option>
                {cases.map((caseItem) => (
                  <option key={caseItem.id} value={caseItem.id}>
                    {caseItem.case_name}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label
                htmlFor="targetFolder"
                style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  fontSize: 'clamp(9px, 2vw, 10px)',
                  fontWeight: 'bold',
                }}
              >
                Select Target Folder
              </label>
              <select
                id="targetFolder"
                value={selectedFolder}
                onChange={(e) => setSelectedFolder(e.target.value)}
                style={{
                  width: '100%',
                  padding: 'clamp(6px, 2vw, 8px)',
                  fontSize: 'clamp(9px, 2vw, 10px)',
                  border: `1px solid ${theme.colors.borderColor}`,
                  borderRadius: '5px',
                  backgroundColor: theme.colors.bgSecondary,
                  color: theme.colors.textPrimary,
                }}
                disabled={isLoading || !selectedCase || isFetchingFolders}
              >
                <option value="">
                  {isFetchingFolders
                    ? 'Loading folders...'
                    : !selectedCase
                    ? displayText('-- Select a case first --')
                    : '-- Select a folder --'}
                </option>
                {folders.map((folder) => (
                  <option key={folder.id} value={folder.id}>
                    {folder.name}
                  </option>
                ))}
              </select>
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer
        style={{
          backgroundColor: theme.colors.bgPrimary,
          borderTop: `1px solid ${theme.colors.borderColor}`,
        }}
      >
        <button
          onClick={handleClose}
          disabled={isLoading}
          style={{
            padding: 'clamp(6px, 2vw, 8px) clamp(12px, 3vw, 16px)',
            fontSize: 'clamp(9px, 2vw, 10px)',
            border: 'none',
            borderRadius: '5px',
            backgroundColor: theme.colors.danger,
            color: theme.colors.textWhite,
            cursor: isLoading ? 'not-allowed' : 'pointer',
            opacity: isLoading ? 0.6 : 1,
          }}
        >
          Cancel
        </button>
        <button
          onClick={handleMove}
          disabled={isLoading}
          style={{
            padding: 'clamp(6px, 2vw, 8px) clamp(12px, 3vw, 16px)',
            fontSize: 'clamp(9px, 2vw, 10px)',
            border: 'none',
            borderRadius: '5px',
            background: theme.gradients.primary,
            color: theme.colors.textWhite,
            cursor: isLoading ? 'not-allowed' : 'pointer',
            opacity: isLoading ? 0.6 : 1,
          }}
        >
          {isLoading ? 'Moving...' : 'Move'}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default MoveModal;